/**
 * Phone Number Utilities for Indonesian WhatsApp Integration
 * Handles validation, formatting, and WhatsApp detection
 */

export interface PhoneNumber {
  number: string;
  source: 'phone_customer' | 'customer_tambak' | 'manual';
  type: 'whatsapp' | 'phone' | 'landline';
  displayLabel?: string;
  isValid: boolean;
  confidence: 'high' | 'medium' | 'low';
}

export interface PhoneValidationResult {
  isValid: boolean;
  isWhatsAppCapable: boolean;
  isMobile: boolean;
  isIndonesian: boolean;
  formatted: string;
  type: 'mobile' | 'landline' | 'invalid';
  confidence: 'high' | 'medium' | 'low';
  carrier?: string;
}

/**
 * Indonesian mobile carrier prefixes for WhatsApp detection
 */
const INDONESIAN_MOBILE_PREFIXES = {
  telkomsel: ['0811', '0812', '0813', '0821', '0822', '0823', '0851', '0852', '0853'],
  indosat: ['0814', '0815', '0816', '0855', '0856', '0857', '0858'],
  xl: ['0817', '0818', '0819', '0859', '0877', '0878'],
  three: ['0895', '0896', '0897', '0898', '0899'],
  smartfren: ['0881', '0882', '0883', '0884', '0885', '0886', '0887', '0888', '0889'],
  axis: ['0831', '0832', '0833', '0838']
};

/**
 * All Indonesian mobile prefixes flattened
 */
const ALL_MOBILE_PREFIXES = Object.values(INDONESIAN_MOBILE_PREFIXES).flat();

/**
 * Indonesian landline area codes (major cities)
 */
const LANDLINE_AREA_CODES = [
  '021', // Jakarta
  '022', // Bandung
  '024', // Semarang
  '031', // Surabaya
  '0361', // Denpasar
  '0274', // Yogyakarta
  '0341', // Malang
  '0411', // Makassar
  '0251', // Bogor
  '0271', // Solo
];

/**
 * Clean phone number by removing all non-numeric characters except +
 */
export const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/[^\d+]/g, '');
};

/**
 * Normalize Indonesian phone number to international format
 */
export const normalizeIndonesianNumber = (phone: string): string => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Already in international format
  if (cleaned.startsWith('+62')) {
    return cleaned;
  }
  
  // Remove country code if present without +
  if (cleaned.startsWith('62')) {
    return `+${cleaned}`;
  }
  
  // Local format starting with 0
  if (cleaned.startsWith('0')) {
    return `+62${cleaned.substring(1)}`;
  }
  
  // Assume it's a local number without 0
  return `+62${cleaned}`;
};

/**
 * Validate and analyze Indonesian phone number
 */
export const validateIndonesianPhoneNumber = (phone: string): PhoneValidationResult => {
  const cleaned = cleanPhoneNumber(phone);
  const normalized = normalizeIndonesianNumber(phone);
  
  // Basic format validation
  if (cleaned.length < 8 || cleaned.length > 15) {
    return {
      isValid: false,
      isWhatsAppCapable: false,
      isMobile: false,
      isIndonesian: false,
      formatted: phone,
      type: 'invalid',
      confidence: 'low'
    };
  }
  
  // Check if Indonesian number
  const isIndonesian = normalized.startsWith('+62');
  if (!isIndonesian) {
    return {
      isValid: true,
      isWhatsAppCapable: false, // Conservative approach for non-Indonesian
      isMobile: false,
      isIndonesian: false,
      formatted: cleaned,
      type: 'invalid',
      confidence: 'low'
    };
  }
  
  // Extract the local part (without +62)
  const localPart = normalized.replace('+62', '0');
  
  // Check mobile prefixes
  const isMobile = ALL_MOBILE_PREFIXES.some(prefix => localPart.startsWith(prefix));
  
  if (isMobile) {
    // Determine carrier for confidence scoring
    let carrier = 'unknown';
    let confidence: 'high' | 'medium' | 'low' = 'medium';
    
    for (const [carrierName, prefixes] of Object.entries(INDONESIAN_MOBILE_PREFIXES)) {
      if (prefixes.some(prefix => localPart.startsWith(prefix))) {
        carrier = carrierName;
        confidence = 'high'; // Known carrier = high confidence
        break;
      }
    }
    
    return {
      isValid: true,
      isWhatsAppCapable: true,
      isMobile: true,
      isIndonesian: true,
      formatted: formatIndonesianNumber(normalized),
      type: 'mobile',
      confidence,
      carrier
    };
  }
  
  // Check landline
  const isLandline = LANDLINE_AREA_CODES.some(code => localPart.startsWith(code));
  
  if (isLandline) {
    return {
      isValid: true,
      isWhatsAppCapable: false,
      isMobile: false,
      isIndonesian: true,
      formatted: formatIndonesianNumber(normalized),
      type: 'landline',
      confidence: 'high'
    };
  }
  
  // Unknown Indonesian number format
  return {
    isValid: true,
    isWhatsAppCapable: false, // Conservative approach
    isMobile: false,
    isIndonesian: true,
    formatted: formatIndonesianNumber(normalized),
    type: 'invalid',
    confidence: 'low'
  };
};

/**
 * Format Indonesian phone number for display
 */
export const formatIndonesianNumber = (phone: string): string => {
  const normalized = normalizeIndonesianNumber(phone);
  
  if (normalized.startsWith('+62')) {
    const localPart = normalized.replace('+62', '');
    
    // Mobile number formatting
    if (localPart.length >= 9) {
      // Format: +62 8XX-XXXX-XXXX
      return `+62 ${localPart.substring(0, 3)}-${localPart.substring(3, 7)}-${localPart.substring(7)}`;
    }
    
    // Landline formatting
    if (localPart.length >= 7) {
      // Format: +62 XX-XXXXXXX
      return `+62 ${localPart.substring(0, 2)}-${localPart.substring(2)}`;
    }
  }
  
  return phone;
};

/**
 * Enhanced phone number processing with multiple numbers support
 */
export const processPhoneNumbers = (
  phoneData: Array<{ number: string; source: 'phone_customer' | 'customer_tambak' }>
): PhoneNumber[] => {
  const processed: PhoneNumber[] = [];
  const seenNumbers = new Set<string>();
  
  phoneData.forEach(({ number, source }) => {
    // Handle comma-separated numbers
    const numbers = number.split(',').map(n => n.trim()).filter(n => n.length > 0);
    
    numbers.forEach(num => {
      const validation = validateIndonesianPhoneNumber(num);
      const normalizedNum = normalizeIndonesianNumber(num);
      
      // Avoid duplicates
      if (seenNumbers.has(normalizedNum)) {
        return;
      }
      seenNumbers.add(normalizedNum);
      
      processed.push({
        number: normalizedNum,
        source,
        type: validation.isWhatsAppCapable ? 'whatsapp' : validation.type === 'landline' ? 'phone' : 'phone',
        displayLabel: validation.formatted,
        isValid: validation.isValid,
        confidence: validation.confidence
      });
    });
  });
  
  // Sort by confidence and WhatsApp capability
  return processed.sort((a, b) => {
    // WhatsApp capable first
    if (a.type === 'whatsapp' && b.type !== 'whatsapp') return -1;
    if (b.type === 'whatsapp' && a.type !== 'whatsapp') return 1;
    
    // Then by confidence
    const confidenceOrder = { high: 3, medium: 2, low: 1 };
    return confidenceOrder[b.confidence] - confidenceOrder[a.confidence];
  });
};

/**
 * Check if customer has any WhatsApp-capable numbers
 */
export const hasWhatsAppNumbers = (phoneNumbers: PhoneNumber[]): boolean => {
  return phoneNumbers.some(phone => phone.type === 'whatsapp' && phone.isValid);
};

/**
 * Get the best WhatsApp number (highest confidence)
 */
export const getBestWhatsAppNumber = (phoneNumbers: PhoneNumber[]): PhoneNumber | null => {
  const whatsappNumbers = phoneNumbers.filter(phone => phone.type === 'whatsapp' && phone.isValid);
  
  if (whatsappNumbers.length === 0) return null;
  
  // Already sorted by confidence in processPhoneNumbers
  return whatsappNumbers[0];
};

/**
 * Generate WhatsApp URL with pre-filled message
 */
export const generateWhatsAppURL = (
  phoneNumber: string, 
  customerName?: string, 
  customMessage?: string
): string => {
  const cleanNumber = cleanPhoneNumber(phoneNumber);
  const normalizedNumber = normalizeIndonesianNumber(cleanNumber);
  
  // Remove + for WhatsApp URL
  const whatsappNumber = normalizedNumber.replace('+', '');
  
  let message = customMessage;
  if (!message && customerName) {
    message = `Halo ${customerName}`;
  }
  
  const baseUrl = `https://wa.me/${whatsappNumber}`;
  
  if (message) {
    return `${baseUrl}?text=${encodeURIComponent(message)}`;
  }
  
  return baseUrl;
};

/**
 * Validate customer has usable phone numbers
 */
export const validateCustomerPhoneCapability = (customer: {
  idcust?: string;
  phone?: string;
  Contacs?: string;
}): {
  hasPhoneData: boolean;
  hasWhatsAppCapable: boolean;
  needsLookup: boolean;
  confidence: 'high' | 'medium' | 'low';
} => {
  // No customer ID - can't look up additional numbers
  if (!customer.idcust) {
    return {
      hasPhoneData: false,
      hasWhatsAppCapable: false,
      needsLookup: false,
      confidence: 'low'
    };
  }
  
  // Check immediate phone data
  const immediateNumbers = [customer.phone, customer.Contacs]
    .filter(Boolean)
    .map(num => num!);
  
  if (immediateNumbers.length === 0) {
    return {
      hasPhoneData: false,
      hasWhatsAppCapable: false,
      needsLookup: true, // Need to check database
      confidence: 'low'
    };
  }
  
  // Validate immediate numbers
  const validatedNumbers = immediateNumbers.map(validateIndonesianPhoneNumber);
  const hasWhatsApp = validatedNumbers.some(v => v.isWhatsAppCapable);
  
  return {
    hasPhoneData: true,
    hasWhatsAppCapable: hasWhatsApp,
    needsLookup: true, // Always check for additional numbers
    confidence: hasWhatsApp ? 'high' : 'medium'
  };
};

/**
 * Create phone number display summary
 */
export const createPhoneNumberSummary = (phoneNumbers: PhoneNumber[]): {
  total: number;
  whatsapp: number;
  phone: number;
  summary: string;
} => {
  const whatsappCount = phoneNumbers.filter(p => p.type === 'whatsapp').length;
  const phoneCount = phoneNumbers.filter(p => p.type === 'phone').length;
  
  let summary = '';
  if (whatsappCount > 0 && phoneCount > 0) {
    summary = `${whatsappCount} WhatsApp, ${phoneCount} Phone`;
  } else if (whatsappCount > 0) {
    summary = `${whatsappCount} WhatsApp`;
  } else if (phoneCount > 0) {
    summary = `${phoneCount} Phone`;
  } else {
    summary = 'No numbers';
  }
  
  return {
    total: phoneNumbers.length,
    whatsapp: whatsappCount,
    phone: phoneCount,
    summary
  };
};